import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import type { MetricChart } from "../../../lib/api";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
);

// Mesma família de cores das StatsGrid (blue/green/orange/red/yellow/purple.300)
// — não uma paleta nova, reaproveita as tonalidades que o resto do sistema já usa.
const PALETTE = ["#63b3ed", "#68d391", "#f6ad55", "#fc8181", "#f6e05e", "#b794f4"];

/** Cor determinística por título do gráfico: com todo gráfico de série única
 * na mesma cor (brand blue), o dashboard inteiro ficava monocromático — cada
 * card passa a puxar uma cor estável e diferente da paleta acima. */
const colorForTitle = (title: string): string => {
  let hash = 0;
  for (let i = 0; i < title.length; i++) hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};

const fmtLabel = (raw: string): string => {
  // Buckets vêm como YYYY-MM-DD (dia/semana) ou YYYY-MM (mês).
  const parts = raw.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
  if (parts.length === 2) return `${parts[1]}/${parts[0].slice(2)}`;
  return raw;
};

export const MetricChartCard = ({ chart, height = 260 }: { chart: MetricChart; height?: number }) => {
  // Mesmo par cardBg/border usado em AddCandidateForm, EditModal, HrAuditLogPage etc.
  const cardBg = useColorModeValue("white", "#0f2040");
  const border = useColorModeValue("gray.200", "whiteAlpha.100");
  const titleColor = useColorModeValue("gray.700", "gray.200");
  const grid = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.08)");
  const tick = useColorModeValue("#6b7280", "#9ca3af");
  const muted = useColorModeValue("gray.400", "gray.500");

  const isHorizontal = chart.type === "horizontalBar";
  const isCircular = chart.type === "doughnut";
  const isTemporal = !isHorizontal && !isCircular;

  const hasData = chart.labels.length > 0 && chart.datasets.some((d) => d.data.some((v) => v !== 0));

  const datasets = chart.datasets.map((ds, i) => {
    if (isCircular) {
      return {
        ...ds,
        backgroundColor: chart.labels.map((_, j) => PALETTE[j % PALETTE.length]),
        borderWidth: 0,
      };
    }
    const color = chart.datasets.length === 1 ? colorForTitle(chart.title) : PALETTE[i % PALETTE.length];
    if (chart.type === "line") {
      return {
        ...ds,
        borderColor: color,
        backgroundColor: `${color}22`,
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2,
        tension: 0.35,
        fill: chart.datasets.length === 1,
      };
    }
    return { ...ds, backgroundColor: color, borderRadius: 4, borderWidth: 0 };
  });

  const options: ChartOptions<any> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: isHorizontal ? ("y" as const) : ("x" as const),
    plugins: {
      // Legenda só agrega quando há mais de uma série ou fatias nomeadas.
      legend: {
        display: isCircular || chart.datasets.length > 1,
        position: isCircular ? ("right" as const) : ("top" as const),
        labels: { color: tick, boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 } },
      },
      tooltip: {
        callbacks: isTemporal
          ? { title: (items: any[]) => (items[0] ? chart.labels[items[0].dataIndex] : "") }
          : undefined,
      },
    },
    scales: isCircular
      ? undefined
      : {
          x: {
            grid: { display: isHorizontal, color: grid },
            ticks: {
              color: tick,
              font: { size: 10 },
              maxRotation: 0,
              autoSkipPadding: 12,
              callback(this: any, value: any) {
                const raw = this.getLabelForValue(value);
                return isHorizontal ? raw : fmtLabel(String(raw));
              },
            },
          },
          y: {
            beginAtZero: true,
            grid: { display: !isHorizontal, color: grid },
            ticks: { color: tick, font: { size: 10 }, precision: 0 },
          },
        },
  };

  const data = { labels: chart.labels.map((l) => (isTemporal ? fmtLabel(l) : l)), datasets };

  return (
    <Box p={4} bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="lg">
      <Text fontSize="sm" fontWeight={600} color={titleColor} mb={3}>
        {chart.title}
      </Text>
      <Box h={`${height}px`} position="relative">
        {!hasData ? (
          <Text fontSize="sm" color={muted} textAlign="center" pt={`${height / 2 - 20}px`}>
            Sem dados no período
          </Text>
        ) : isCircular ? (
          <Doughnut data={data} options={options} />
        ) : chart.type === "line" ? (
          <Line data={data} options={options} />
        ) : (
          <Bar data={data} options={options} />
        )}
      </Box>
    </Box>
  );
};
